import { useState, useCallback } from 'react'
import { api, syncApi } from '../../../../services/api'
import { FranchisePartner } from './useFranchisePartnersData'
import { useSyncProgress } from '../../../../hooks/useSyncProgress'

export type SyncSystem = 'lightspeed' | 'xero' | 'skimmer'

interface UseSyncOperationsReturn {
  triggerSync: (fp: FranchisePartner, system: SyncSystem, syncType?: string, silent?: boolean) => Promise<void>
  triggerSyncAll: (fp: FranchisePartner) => Promise<void>
  syncingFps: { [key: string]: boolean }
  activeSyncs: { [key: string]: any }
  fetchActiveSyncs: () => Promise<void>
  currentSyncOperation: string | null
  syncProgress: any
  syncProgressError: any
}

export const useSyncOperations = (
  onSyncSuccess?: (message: string) => void,
  onSyncError?: (message: string) => void
): UseSyncOperationsReturn => {
  const [syncingFps, setSyncingFps] = useState<{ [key: string]: boolean }>({})
  const [activeSyncs, setActiveSyncs] = useState<{ [key: string]: any }>({})
  const [activeOperationIds, setActiveOperationIds] = useState<{ [key: string]: string }>({})
  const [currentSyncOperation, setCurrentSyncOperation] = useState<string | null>(null)

  // Use sync progress hook
  const { progress: syncProgress, error: syncProgressError } = useSyncProgress(
    currentSyncOperation,
    {
      onComplete: (progress) => {
        console.log('✅ Sync completed:', progress)
        setCurrentSyncOperation(null)
        fetchActiveSyncs()
      },
      onError: (error) => {
        console.error('❌ Sync error:', error)
        setCurrentSyncOperation(null)
        if (onSyncError) {
          onSyncError(error)
        }
      }
    }
  )

  // Fetch active syncs to show real-time sync status
  const fetchActiveSyncs = useCallback(async () => {
    try {
      console.log('🔄 Fetching active syncs for FP list...')

      const response = await syncApi.getAllActiveSyncs()
      const activeSyncsList = response.data

      // Convert array to nested object keyed by fp_code and sync_system
      // This allows tracking multiple concurrent syncs for the same FP
      const activeSyncsMap: { [key: string]: { [system: string]: any } } = {}
      activeSyncsList.forEach((sync: any) => {
        if (!activeSyncsMap[sync.fp_code]) {
          activeSyncsMap[sync.fp_code] = {}
        }
        activeSyncsMap[sync.fp_code][sync.sync_system] = sync
      })

      console.log('🔄 Active syncs loaded:', Object.keys(activeSyncsMap).length, 'FPs with active syncs')
      setActiveSyncs(activeSyncsMap)
    } catch (err) {
      console.error('❌ Failed to fetch active syncs:', err)
    }
  }, [])

  // Generic sync handler with explicit system validation.
  // `silent` suppresses the per-system result toast - set by triggerSyncAll so
  // a multi-vendor "Sync all" surfaces ONE aggregate toast, not one per vendor.
  const triggerSync = useCallback(async (
    fp: FranchisePartner,
    system: SyncSystem,
    syncType: string = 'incremental',
    silent: boolean = false
  ): Promise<void> => {
    if (!fp.fp_code || fp.fp_code.toLowerCase() === 'undefined' || fp.fp_code.toLowerCase() === 'null') {
      const errorMessage = '❌ Invalid franchise partner code. Please refresh the page and try again.'
      if (onSyncError) onSyncError(errorMessage)
      throw new Error(errorMessage)
    }

    if (!system || !['lightspeed', 'xero', 'skimmer'].includes(system)) {
      const errorMessage = '❌ Invalid sync system. Please try again.'
      if (onSyncError) onSyncError(errorMessage)
      throw new Error(errorMessage)
    }

    setSyncingFps(prev => ({ ...prev, [fp.fp_code]: true }))

    try {
      console.log(`🔄 Starting ${system} sync for ${fp.franchise_name}`)

      const response = await api.post(`/sync/${system}/${fp.fp_code}`, {
        sync_type: syncType
      })

      // Get operation ID from response
      const operationId = response.data?.operation_id
      if (operationId) {
        setCurrentSyncOperation(operationId)
        setActiveOperationIds(prev => ({ ...prev, [fp.fp_code]: operationId }))
      }

      // Vendor-specific toast only when the user triggered this vendor
      // directly; triggerSyncAll passes silent and owns the aggregate toast.
      const successMessage = `${system.charAt(0).toUpperCase() + system.slice(1)} sync started for ${fp.franchise_name}`
      if (!silent && onSyncSuccess) onSyncSuccess(successMessage)

      console.log(`${system} sync started successfully for ${fp.fp_code}:`, response)

      setTimeout(() => fetchActiveSyncs(), 1000)

    } catch (error: any) {
      console.error(`Failed to start ${system} sync for ${fp.fp_code}:`, error)
      const errorMessage = `${system.charAt(0).toUpperCase() + system.slice(1)} sync failed for ${fp.franchise_name}`
      if (!silent && onSyncError) onSyncError(errorMessage)
      throw error
    } finally {
      setSyncingFps(prev => ({ ...prev, [fp.fp_code]: false }))
    }
  }, [fetchActiveSyncs, onSyncError, onSyncSuccess])

  const triggerSyncAll = useCallback(async (fp: FranchisePartner): Promise<void> => {
    // Validate franchise partner code
    if (!fp.fp_code || fp.fp_code.toLowerCase() === 'undefined' || fp.fp_code.toLowerCase() === 'null') {
      const errorMessage = 'Invalid franchise partner code. Please refresh the page and try again.'
      if (onSyncError) onSyncError(errorMessage)
      return
    }

    // Determine which systems have complete connections
    // NOTE: Skimmer excluded as it's not live yet
    const connectedSystems: SyncSystem[] = []
    if (fp.has_complete_lightspeed_connection) connectedSystems.push('lightspeed')
    if (fp.has_complete_xero_connection) connectedSystems.push('xero')
    // Skimmer intentionally excluded even if connected - not live yet
    // if (fp.has_complete_skimmer_connection) connectedSystems.push('skimmer')

    if (connectedSystems.length === 0) {
      const errorMessage = 'No complete system connections found. Cannot sync all systems.'
      if (onSyncError) onSyncError(errorMessage)
      return
    }

    setSyncingFps(prev => ({ ...prev, [fp.fp_code]: true }))
    console.log(`Syncing ${connectedSystems.length} systems for ${fp.franchise_name}: ${connectedSystems.join(', ')} (Skimmer excluded - not live yet)`)

    try {
      // Trigger syncs for all connected systems in parallel. silent=true so
      // each vendor doesn't fire its own toast - we emit one aggregate below.
      const syncPromises = connectedSystems.map(system =>
        triggerSync(fp, system, 'incremental', true).catch(error => {
          console.error(`Failed to sync ${system}:`, error)
          return { system, error }
        })
      )

      // Wait for all syncs to start (not complete)
      const results = await Promise.allSettled(syncPromises)

      // Count successful sync starts
      const successfulSyncs = results.filter(result => result.status === 'fulfilled').length
      const failedSyncs = results.length - successfulSyncs

      if (successfulSyncs > 0) {
        let message = `${successfulSyncs} sync${successfulSyncs > 1 ? 's' : ''} started for ${fp.franchise_name}`
        if (failedSyncs > 0) {
          message += ` (${failedSyncs} failed to start)`
        }
        if (onSyncSuccess) onSyncSuccess(message)
        setTimeout(() => fetchActiveSyncs(), 1000)
      } else {
        const errorMessage = `Sync failed for ${fp.franchise_name}`
        if (onSyncError) onSyncError(errorMessage)
      }

    } catch (error: any) {
      console.error(`Failed to start all systems sync for ${fp.fp_code}:`, error)
      const errorMessage = `Sync failed for ${fp.franchise_name}`
      if (onSyncError) onSyncError(errorMessage)
    } finally {
      setSyncingFps(prev => ({ ...prev, [fp.fp_code]: false }))
    }
  }, [triggerSync, fetchActiveSyncs, onSyncError, onSyncSuccess])

  return {
    triggerSync,
    triggerSyncAll,
    syncingFps,
    activeSyncs,
    fetchActiveSyncs,
    currentSyncOperation,
    syncProgress,
    syncProgressError
  }
}